'use client';

import { FormEvent, useRef } from 'react'
import { notification } from 'antd';
import type { NotificationArgsProps } from 'antd';
import { shop, employment_question, employment_job } from "@/types/graphql";
import FAIcon from "@/base-components/icons";
import React, { useMemo } from 'react';
import Checkbox from "@/ui-components/checkbox";
import RadioButton from "@/ui-components/radio-button";

interface EmploymentResponse
{
	status: 'success' | 'error';
	message: string;
}

function question_type(type: number, idx: number, options?: string | null, required?: boolean | null | undefined)
{
	switch (type)
	{

		// Text line
		case 1: {
			return <input
				name={'question-' + idx}
				aria-labelledby="firstname"
				required={required ?? false}
				className="w-full rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 px-3 py-3 outline-none ring-primary-300 transition duration-100 focus:ring"
			/>
		}

		// Text area
		case 2: {
			return <textarea
				name={'question-' + idx}
				aria-labelledby="firstname"
				required={required ?? false}
				className="w-full h-32 rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 px-3 py-2 outline-none ring-primary-300 transition duration-100 focus:ring"
			/>
		}

		// Yes/No
		case 3: {
			return <div className="flex items-start flex-wrap w-full">
				<div className="w-1/2 flex items-center">
					<RadioButton
						required={required ?? false}
						id={'yes-' + idx}
						value="Yes"
						name={'question-' + idx}
					/>
					<label
						htmlFor={'yes-' + idx}
						className="ms-2">
						Yes
					</label>
				</div>
				<div className="w-1/2 flex items-center">
					<RadioButton
						required={required ?? false}
						id={'no-' + idx}
						value="No"
						name={'question-' + idx}
					/>
					<label
						htmlFor={'no-' + idx}
						className="ms-2">
						No
					</label>
				</div>
			</div>
		}

		// Radio buttons
		case 4: {
			if (options !== undefined && options != null && options.length > 0)
			{
				return <div className="flex items-start flex-wrap w-full">{
					options?.split(",")?.map((option: string, optionIdx: number) => (

						<div
							key={optionIdx}
							className="w-full lg:w-1/2 flex items-center mb-2">
							<RadioButton
								required={required ?? false}
								id={'radio-' + option + '-' + optionIdx}
								value={option}
								name={'question-' + idx}
							/>
							<label
								htmlFor={'radio-' + option + '-' + optionIdx}
								className="ms-2">
								{option}
							</label>
						</div>

					))
				}
				</div>
			}
		}

		// Checkboxes
		case 5: {
			if (options !== undefined && options != null && options.length > 0)
			{

				return <div className="flex items-start flex-wrap w-full">{
					options?.split(",")?.map((option: string, optionIdx: number) => (

						<div
							key={optionIdx}
							className="w-full lg:w-1/2 flex items-center mb-2">
							<Checkbox
								required={required ?? false}
								id={'checkbox-' + option + '-' + optionIdx}
								value={option}
								name={'question-' + idx}
							/>
							<label
								htmlFor={'checkbox-' + option + '-' + optionIdx}
								className="ms-2">
								{option}
							</label>
						</div>

					))
				}
				</div>
			}
		}
	}
}

export default function FormEmployment({ title_styles = '', data, columns = 'two' }: { title_styles: string, data: shop, columns?: string })
{

	const resumeRef = useRef<HTMLInputElement>(null);

	const employment_questions = data?.employment_questions?.filter(_ =>
		_?.enabled && _?.employment_question_type_id && _?.question
	);

	const employment_jobs = data?.employment_jobs?.filter(_ =>
		_?.enabled && _?.title
	);

	type NotificationType = 'success' | 'error';

	const [api, contextHolder] = notification.useNotification();
	type NotificationPlacement = NotificationArgsProps['placement'];
	const Context = React.createContext({ name: 'Default' });

	const openNotification = (placement: NotificationPlacement, message: string, type: NotificationType) =>
	{
		const capitalized = type.charAt(0).toUpperCase() + type.slice(1);
		api[type]({
			message: capitalized,
			description: <Context.Consumer>{() => message}</Context.Consumer>,
			placement,
		});
	};

	async function getBase64(file: File): Promise<string>
	{
		return new Promise((resolve, reject) =>
		{
			var reader = new FileReader();
			reader.onload = () =>
			{
				if (reader && reader.result)
				{
					const result = reader.result.toString();
					const base64Data = result.split(',', 2);
					if (base64Data.length === 2 && base64Data[1])
					{
						return resolve(base64Data[1]);
					}
					else
					{
						openNotification('bottomRight', "The file data is empty", "error");
						return reject(new Error("File data is empty"));
					}
				}
			}

			reader.onerror = error =>
			{
				openNotification('bottomRight', "Cannot read the attached file:", "error");
				reject(error);
			};

			reader.readAsDataURL(file);

		});
	}

	function getjson<T>(text: string): T | null
	{
		try
		{
			return JSON.parse(text) as T;
		}
		catch (error)
		{
			openNotification(
				'bottomRight',
				"Error parsing JSON data: " + error,
				"error"
			);
			console.log('Error parsing JSON:', error);
			return null;
		}
	}

	const contextValue = useMemo(() => ({ name: 'Contact Form' }), []);

	async function onSubmit(event: FormEvent<HTMLFormElement>)
	{

		event.preventDefault()

		const formData = new FormData(event.currentTarget);

		// According to backend requirements — ensure mandatory fields are always included, even if empty
		const mandatory: string[] = [
			'driverLicense',
			'ownTransportation',
			'firstName',
			'lastName',
			'email',
			'phone',
			"contentType",
			"fileName",
			"fileData",
		];

		for (const question of mandatory)
		{
			const answer = formData.get(question)?.toString() || '';
			formData.set(question, answer);
		}

		// console.log('FormData entries:');
		// console.table([...formData.entries()]);

		// Custom questions
		const _questions: string[] = [];
		for (const [idx, question] of employment_questions?.entries() ?? [])
		{
			question &&
				_questions.push(`${question?.question?.toString()}\n${formData.getAll(`question-${idx}`)?.join(',').trim() || '—'}`);
			formData.delete(`question-${idx}`);
		}

		const questions: string = _questions.join('\n\n');
		// console.log('Assembled questions:');
		// console.table(questions);

		formData.append('questions', questions);
		// console.log('FormData with assembled questions:');
		// console.table([...formData.entries()]);

		const files = resumeRef.current?.files;

		const filetypesMap: Record<string, string> = {
			'text/plain': 'txt',
			'application/msword': 'doc',
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
			'application/pdf': 'pdf'
		};

		if (files && files?.length > 0)
		{
			const file = files[0];

			const filetype = filetypesMap[file?.type || ''] || '';
			if (!filetype)
			{
				openNotification('bottomRight', "Only TXT, DOC, DOCX, PDF files allowed", "error");
				return;
			}

			formData.set("contentType", filetype);
			formData.set("fileName", file?.name);
			formData.set('fileData', await getBase64(file));
		}

		const url = "https://services.autovitals.com/contact.asmx/SubmitEmploymentRequest";
		const headers = { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' };
		const body = new URLSearchParams(formData as any).toString();
		// console.log('Final query string:', query);

		try
		{
			const response = await fetch(
				url,
				{ method: 'POST', headers: headers, body: body }
			);

			let responseText = await response.text();

			// console.log('SubmitEmploymentRequest status:', response.status);
			// console.log('SubmitEmploymentRequest text:', rawText);

			if (!response.ok)
			{
				openNotification(
					'bottomRight',
					`Server error ${response.status}: (${responseText})`,
					'error'
				);
				return;
			}

			if (responseText.startsWith('harry({'))
			{
				responseText = responseText.replace('harry({', '{')
			}
			if (responseText.endsWith('});'))
			{
				responseText = responseText.replace('});', '}')
			}

			const jsonResponse = getjson<EmploymentResponse>(responseText);

			jsonResponse &&
				openNotification(
					'bottomRight',
					jsonResponse?.message,
					jsonResponse?.status
				);

		}
		catch (error: any)
		{
			openNotification('bottomRight',
				error.message || 'An unexpected error occurred',
				'error'
			);
		}

	}

	const columns_per_row: any = (columns === 'one' || columns === 'two') &&
	{
		container: {
			one: 'grid-cols-1 max-w-screen-md mx-auto',
			two: 'w-full',
		},
		questions: {
			one: 'grid-cols-1',
			two: 'md:grid-cols-2',
		}
	}

	return (
		<form
			onSubmit={onSubmit}
			className={`${columns_per_row?.container[columns]}
			flex flex-wrap`}>

			{/* Title */}
			<label
				htmlFor="jobTitle"
				className="inline-block mb-8">
				<h3 className={`${title_styles}
				dark:text-white`}>

					Position Applied For

				</h3>
			</label>

			{/* Desired position */}
			<div className={`w-full mb-8`}>

				<select
					id="jobTitle"
					name="jobTitle"
					className="w-full rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 px-3 py-4 outline-none ring-primary-300 transition duration-100 focus:ring">
					<option value="default">
						Choose a desired position
						{
							data?.info?.employment_position_required &&
							<>*</>
						}
					</option>
					{
						employment_jobs?.map((position: employment_job, idx: number) => (
							<option
								key={idx}
								value={position?.title ?? ''}>

								{position?.title}

							</option>
						))
					}
				</select>

			</div>

			{/* Personal data */}
			<div className={`${columns_per_row?.questions[columns]}
				w-full grid gap-8 mb-20`}>

				<div>
					<label htmlFor="firstName" className="mb-1 inline-block text-p5">
						First name
						{
							data?.info?.employment_first_name_required &&
							<span>*</span>
						}
					</label>
					<input id="firstName" required={data?.info?.employment_first_name_required ?? false} name="firstName" autoComplete="given-name" className="w-full rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 px-3 py-3 outline-none ring-primary-300 transition duration-100 focus:ring" />
				</div>

				<div>
					<label htmlFor="lastName" className="mb-1 inline-block text-p5">
						Last name
						{
							data?.info?.employment_last_name_required &&
							<span>*</span>
						}
					</label>
					<input id="lastName" required={data?.info?.employment_last_name_required ?? false} name="lastName" autoComplete="family-name" className="w-full rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 px-3 py-3 outline-none ring-primary-300 transition duration-100 focus:ring" />
				</div>

				<div className="">
					<label htmlFor="email" className="mb-1 inline-block text-p5">
						Email
						{
							data?.info?.employment_email_required &&
							<span>*</span>
						}
					</label>
					<input id="email" required={data?.info?.employment_email_required ?? false} name="email" autoComplete="email" className="w-full rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 px-3 py-3 outline-none ring-primary-300 transition duration-100 focus:ring" />
				</div>

				<div className="">
					<label htmlFor="phone" className="mb-1 inline-block text-p5">
						Phone
						{
							data?.info?.employment_phone_required &&
							<span>*</span>
						}
					</label>
					<input id="phone" required={data?.info?.employment_phone_required ?? false} name="phone" autoComplete="tel" className="w-full rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 px-3 py-3 outline-none ring-primary-300 transition duration-100 focus:ring" />
				</div>

				<div className="">
					<label htmlFor="resume" className="mb-1 inline-block text-p5">
						Choose resume file
						{
							data?.info?.employment_resume_required &&
							<span>*</span>
						}
					</label>
					<input ref={resumeRef} id="resume" name="resume" required={data?.info?.employment_resume_required ?? false} autoComplete="off" type="file" className="w-full rounded border border-neutral-300 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 dark:text-neutral-200 px-3 py-3 outline-none ring-primary-300 transition duration-100 focus:ring" />
				</div>

			</div>

			{/* Addiional questions */}
			{
				(data?.info?.employment_ask_driver_license ||
					data?.info?.employment_ask_transportation ||
					data?.employment_questions?.some(_ => _?.enabled && _?.employment_question_type_id)) &&

				<>
					<h3 className={`${title_styles}
						w-full mb-16 md:mb-20 dark:text-white`}>

						Please answer the following questions

					</h3>

					<div className={`${columns_per_row?.questions[columns]}
						w-full grid gap-16 md:gap-20 mb-20`}>

						{/* Drivers license */}
						{
							data?.info?.employment_ask_driver_license &&
							<div className="flex flex-wrap w-full mb-8 md:mb-0">

								<h4 className={`${title_styles}
								w-full mb-2 text-h6 dark:text-white`}>
									Do you have a valid drivers license?
									{
										data?.info?.employment_ask_driver_license_required &&
										<span>*</span>
									}
								</h4>

								<div className="w-1/2 flex items-center">
									<RadioButton
										required={data?.info?.employment_ask_driver_license_required ?? false}
										id="drivers-license-yes"
										value="yes"
										name="driverLicense"
									/>
									<label
										htmlFor="drivers-license-yes"
										className="ms-2">
										Yes
									</label>
								</div>

								<div className="w-1/2 flex items-center">
									<RadioButton
										required={data?.info?.employment_ask_driver_license_required ?? false}
										id="drivers-license-no"
										value="no"
										name="driverLicense"
									/>
									<label
										htmlFor="drivers-license-no"
										className="ms-2">
										No
									</label>
								</div>

							</div>
						}

						{/* Own transportation */}
						{
							data?.info?.employment_ask_transportation &&
							<div className="flex flex-wrap w-full">

								<h4 className={`${title_styles}
								w-full mb-2 text-h6 dark:text-white`}>
									Do you have your own transportation?
									{
										data?.info?.employment_ask_transportation_required &&
										<span>*</span>
									}
								</h4>

								<div className="w-1/2 flex items-center">
									<RadioButton
										required={data?.info?.employment_ask_transportation_required ?? false}
										id="own-transportation-yes"
										value="yes"
										name="ownTransportation"
									/>
									<label
										htmlFor="own-transportation-yes"
										className="ms-2">
										Yes
									</label>
								</div>

								<div className="w-1/2 flex items-center">
									<RadioButton
										required={data?.info?.employment_ask_transportation_required ?? false}
										id="own-transportation-no"
										value="no"
										name="ownTransportation"
									/>
									<label
										htmlFor="own-transportation-no"
										className="ms-2">
										No
									</label>
								</div>
							</div>
						}

					</div>

					{/* Custom questions */}
					<ul className={`${columns_per_row['questions'][columns]}
						w-full grid place-items-start gap-16 md:gap-20 mb-16 md:mb-20`}>
						{
							employment_questions?.map((question: employment_question, idx: number) => (
								<li
									key={question?.sort}
									className="flex flex-wrap w-full">

									<label className="w-full">

										<h4 className={`${title_styles}
											mb-4 text-h6 dark:text-white`}>
											{question?.question}
										</h4>

										{question_type(
											question?.employment_question_type_id,
											idx,
											question?.options,
											question?.required
										)}

									</label>
								</li>
							))
						}
					</ul>

				</>
			}

			{/* Form button */}
			<div className="flex flex-wrap w-full items-center justify-between">

				<input type="hidden" name="shopId" value={process.env.NEXT_PUBLIC_SHOP_ID} />
				<input type="hidden" name="cb" value="harry" />
				<input type="hidden" name="gRecaptchaResponse" value="dummy" />

				<Context.Provider value={contextValue}>
					{contextHolder}
					<button type="submit" className="inline-flex items-center justify-center w-full md:w-auto rounded bg-primary-500 px-8 py-3 font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring active:bg-primary-500 cursor-pointer">
						Submit
						<FAIcon type="solid" nameIco='fa-envelop' customClass="h-4 text-white ml-2" />
					</button>
				</Context.Provider>

				<p className="text-p6 mt-8 md:mt-0">
					*Message and data rates may apply. Text STOP to opt-out or HELP for help.
				</p>

			</div>

		</form>
	)
}
